import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Globe, Heart, Lightbulb, Target, Mic, PenLine, Video, Users } from "lucide-react";
import { SEO } from "@/components/SEO";

const VALUES = [
  { icon: Target, title: "Rigueur éditoriale", desc: "Chaque article est documenté, vérifié et enrichi par des experts du terrain éducatif franco-américain." },
  { icon: Heart, title: "Bienveillance", desc: "Nous accompagnons les familles sans jugement, en tenant compte de la complexité de chaque parcours." },
  { icon: Globe, title: "Ouverture internationale", desc: "Notre regard se porte au-delà des frontières pour explorer les meilleures pratiques éducatives mondiales." },
  { icon: Lightbulb, title: "Innovation", desc: "Nous intégrons les avancées technologiques et pédagogiques pour rester à la pointe de l'éducation." },
];

const TIMELINE = [
  { year: "2019", event: "Création d'OFALycée, premier lycée franco-américain 100\u00a0% en ligne." },
  { year: "2020", event: "Première rentrée scolaire et lancement des OFA Lives, des sessions en direct pour échanger avec les familles." },
  { year: "2022", event: "Plus de 80 Lives réalisés et une communauté grandissante de familles accompagnées." },
  { year: "2024", event: "Création de la plateforme éditoriale pour centraliser articles, lives, analyses et ressources." },
  { year: "2025", event: "54 articles et 117 lives publiés, 4 axes éditoriaux et une médiathèque vidéo complète." },
];

const FORMATS = [
  { icon: PenLine, title: "Articles de fond", desc: "Analyses approfondies, guides pratiques et décryptages sur les enjeux éducatifs." },
  { icon: Mic, title: "Lives & Podcasts", desc: "Sessions en direct avec des experts, parents et professionnels de l'éducation." },
  { icon: Video, title: "Vidéos", desc: "Reportages, témoignages et tutoriels pour accompagner les familles au quotidien." },
  { icon: BookOpen, title: "Ressources", desc: "Fiches pratiques, check-lists et outils pour naviguer le système éducatif international." },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="À Propos — Qui sommes-nous ?"
        description="Découvrez l'histoire d'OFALycée Éditorial, notre mission, nos valeurs et l'équipe derrière le média de l'éducation bilingue et internationale."
        url="/a-propos"
      />
      <div data-testid="about-page">

        {/* HERO */}
        <section className="relative overflow-hidden bg-[#14274B] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(45,120,246,0.2),_transparent_60%)]" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 relative z-10">
            <div className="max-w-3xl">
              <span className="text-sm font-mono tracking-widest uppercase text-ofa-blue mb-4 block" data-testid="about-label">À propos</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-8" data-testid="about-title">
                Le média qui éclaire les parcours éducatifs internationaux
              </h1>
              <p className="text-lg md:text-xl text-white/60 leading-relaxed max-w-2xl">
                Depuis 2019, OFALycée accompagne les familles francophones à travers le monde.
                Notre plateforme éditoriale prolonge cette mission en produisant des contenus de référence
                sur l'éducation bilingue, la mobilité internationale et l'innovation pédagogique.
              </p>
            </div>
          </div>
        </section>

        {/* NOTRE HISTOIRE */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="our-story">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Notre histoire</span>
              <h2 className="font-heading text-3xl md:text-4xl mb-6">Trois expériences, une conviction</h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  OFALycée n'est pas né d'une intuition abstraite mais de <strong className="text-foreground">trois expériences concrètes</strong> qui ont convergé vers une même évidence.
                </p>

                <div className="border-l-2 border-primary pl-5">
                  <h3 className="font-heading text-lg text-foreground mb-2">La transférabilité prouvée sur le terrain</h3>
                  <p>
                    Avant OFALycée, les fondateurs ont accompagné des élèves qui n'avaient jamais été scolarisés
                    dans le système français — mais qui avaient appris à lire et écrire le français par d'autres voies.
                    En arrivant en Première ou Terminale, ils passaient le baccalauréat en un ou deux ans
                    — et ils réussissaient tous brillamment. Certains faisaient même de cette année de préparation
                    au bac une véritable <em>gap year</em>, alliant immersion américaine et excellence académique française. Leur secret : ils utilisaient les acquis et compétences du système américain
                    pour enrichir et nourrir leur parcours dans le système français. La preuve était faite que
                    les connaissances et compétences sont transférables d'un système à l'autre.
                  </p>
                </div>

                <div className="border-l-2 border-primary pl-5">
                  <h3 className="font-heading text-lg text-foreground mb-2">L'expérience transformatrice du online</h3>
                  <p>
                    Pendant deux ans, Géraldine a suivi un Master en <em>Educational Leadership and Administration</em>
                    à la George Washington University — entièrement en ligne. Loin d'être une expérience froide ou désincarnée,
                    ce fut une révélation : proximité avec les professeurs, disponibilité, échanges enrichissants.
                    Créer du lien à distance était non seulement possible, mais naturel. Cette conviction est devenue
                    l'un des piliers fondateurs d'OFALycée.
                  </p>
                </div>

                <div className="border-l-2 border-primary pl-5">
                  <h3 className="font-heading text-lg text-foreground mb-2">Une amitié de 20 ans, une même passion</h3>
                  <p>
                    OFALycée est né de la collaboration de trois professeurs qui se connaissent depuis plus de vingt ans
                    et qui partagent les mêmes valeurs et la même passion pour l'éducation :
                  </p>
                </div>

                <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mt-2">
                  <p className="text-foreground font-medium leading-relaxed">
                    Nous permettons aux élèves qui veulent profiter pleinement de l'immersion américaine
                    de ne plus avoir à choisir entre les deux systèmes. Avec OFALycée, ils peuvent faire les deux,
                    au plus haut niveau de bilinguisme et de biculturalisme possible.
                    <strong> En cela, OFALycée révolutionne l'enseignement bilingue et biculturel.</strong>
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_43cffa8a-b7d6-4554-a2b3-1f6490ed10a7/artifacts/qsqps9qg_iStock-1312418309.jpg"
                alt="OFALycée — éducation internationale connectée"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#14274B] text-white p-6 hidden md:block">
                <span className="block font-heading text-3xl">2019</span>
                <span className="text-sm text-white/60">Année de création</span>
              </div>
            </div>
          </div>

          {/* Fondateurs */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8" data-testid="founders">
            {[
              { name: "Géraldine Guillermin", role: "Cofondatrice", desc: "Master en Educational Leadership & Administration (GW University). Convaincue que le lien éducatif transcende la distance." },
              { name: "Valérie Burnick", role: "Cofondatrice", desc: "Plus de 20 ans d'expérience dans l'enseignement français à l'international. La rigueur au service de l'ouverture." },
              { name: "Olivier Saint-Vincent", role: "Cofondateur", desc: "Enseignant passionné et auteur prolifique. Sa plume alimente le blog et nourrit la réflexion éditoriale d'OFALycée." },
            ].map((f, i) => (
              <div key={i} className="text-center p-6 border border-border rounded-xl hover:border-primary/30 transition-colors" data-testid={`founder-${i}`}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#14274B] flex items-center justify-center">
                  <Users className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-lg mb-1">{f.name}</h3>
                <span className="text-xs font-mono tracking-wider uppercase text-primary block mb-3">{f.role}</span>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TIMELINE */}
        <section className="bg-slate-50 py-16 md:py-24" data-testid="timeline">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14">
              <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Parcours</span>
              <h2 className="font-heading text-3xl md:text-4xl">Les étapes clés</h2>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-px" />
              {TIMELINE.map((item, i) => (
                <div key={i} className={`relative flex items-start gap-8 mb-10 last:mb-0 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className={`hidden md:block w-1/2 ${i % 2 === 0 ? "text-right pr-12" : "text-left pl-12"}`}>
                    <span className="font-heading text-2xl text-navy-DEFAULT">{item.year}</span>
                    <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.event}</p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary border-4 border-white shadow-sm z-10 mt-2" />
                  <div className="md:hidden pl-10">
                    <span className="font-heading text-xl text-navy-DEFAULT">{item.year}</span>
                    <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{item.event}</p>
                  </div>
                  <div className="hidden md:block w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* NOS VALEURS */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="values">
          <div className="text-center mb-14">
            <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Ce qui nous guide</span>
            <h2 className="font-heading text-3xl md:text-4xl">Nos valeurs</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="group" data-testid={`value-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#14274B] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-heading text-lg mb-2">{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CHIFFRES */}
        <section className="bg-[#14274B] text-white py-16 md:py-20" data-testid="impact-numbers">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14">
              <span className="text-sm font-mono tracking-widest uppercase text-ofa-blue mb-3 block">Notre impact</span>
              <h2 className="font-heading text-3xl md:text-4xl">En quelques chiffres</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {[
                { value: "54+", label: "Articles publiés" },
                { value: "117+", label: "Lives réalisés" },
                { value: "1\u00a0600+", label: "Élèves accompagnés" },
              ].map((s, i) => (
                <div key={i} className="text-center" data-testid={`impact-stat-${i}`}>
                  <span className="block font-heading text-4xl md:text-5xl text-ofa-blue">{s.value}</span>
                  <span className="text-sm text-white/50 mt-2 block">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* NOS FORMATS */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="formats">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Notre approche éditoriale</span>
              <h2 className="font-heading text-3xl md:text-4xl mb-6">Des formats variés pour chaque besoin</h2>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Notre rédaction produit des articles et des lives diversifiés pour répondre aux questions concrètes
                des familles, qu'il s'agisse de choisir un cursus, de préparer une transition
                ou de comprendre les évolutions du paysage éducatif mondial.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {FORMATS.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className="flex gap-4" data-testid={`format-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-base mb-1">{f.title}</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/8127694/pexels-photo-8127694.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Équipe éditoriale"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </section>

        {/* OFALYCEE L'ECOLE */}
        <section className="bg-slate-50 py-16 md:py-24" data-testid="school-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_43cffa8a-b7d6-4554-a2b3-1f6490ed10a7/artifacts/f81wc8i8_iStock-2148308297.jpg"
                  alt="Élève OFALycée en ligne"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">L'école derrière le média</span>
                <h2 className="font-heading text-3xl md:text-4xl mb-6">OFALycée, le lycée franco-américain en ligne</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    OFALycée est le premier lycée franco-américain entièrement en ligne,
                    <strong className="text-foreground"> accrédité par Cognia</strong>, l'un des organismes d'accréditation
                    les plus reconnus au monde. Il propose deux voies distinctes aux élèves francophones :
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong className="text-foreground">La voie Éducation Nationale</strong> — pour les élèves qui souhaitent suivre le programme officiel français.</li>
                    <li><strong className="text-foreground">La voie OFALycée</strong> — un cursus bilingue premium qui délivre le <em>Bilingual High School Diploma</em>, le diplôme propre à OFALycée.</li>
                  </ul>
                  <p>
                    Notre approche pédagogique combine la rigueur académique du système français
                    et la flexibilité du modèle américain, avec un encadrement personnalisé
                    et des classes à taille humaine.
                  </p>
                </div>
                <div className="mt-8">
                  <a href="https://ofalycee.org" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-6" data-testid="school-cta-btn">
                      Découvrir OFALycée <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24" data-testid="contribute-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Rejoignez-nous</span>
            <h2 className="font-heading text-3xl md:text-4xl mb-6">Vous souhaitez contribuer ?</h2>
            <p className="text-muted-foreground mb-10 max-w-xl mx-auto leading-relaxed">
              Experts en éducation, parents expatriés, pédagogues : nous accueillons vos témoignages,
              analyses et retours d'expérience pour enrichir notre communauté.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="rounded-full px-8 bg-[#14274B] hover:bg-[#14274B]/90" data-testid="contribute-btn">
                  Nous contacter <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/blog">
                <Button variant="outline" className="rounded-full px-8" data-testid="explore-articles-btn">
                  Explorer les articles
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
